import {
  ApprovalMode,
  ApprovalStrategy,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ApproverCandidate } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'

import { Box, Flex, PrimaryButton, chakra } from '@enechain/ecloud-designsystem'

import type { StageLocal } from '~/components/approval/stage'
import { ApprovalModeTag } from '~/components/common/ApprovalModeTag'
import { HelpIcon } from '~/components/common/HelpIcon'
import { Modal } from '~/components/common/Modal'
import { convertAreaLabel } from '~/models/area'
import {
  type CellKey,
  makeNoSectionCellKey,
} from '~/pages/AdminApprovalFlow/constants/processMap'
import type { CellLocal } from '~/pages/AdminApprovalFlow/models/reducer'

/** table.md §0: TH = Surface.tableHeader / Text.midEmphasis / 600、罫線 Border.lowEmphasis。 */
const HEADER_CELL_PROPS = {
  padding: '8px 12px',
  color: 'Text.midEmphasis',
  fontWeight: 'semibold',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  background: 'Surface.tableHeader',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'Border.lowEmphasis',
} as const

const BODY_CELL_PROPS = {
  padding: '8px 12px',
  color: 'Text.highEmphasis',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'Border.lowEmphasis',
} as const

type Props = {
  isOpen: boolean
  onClose: () => void
  processLabel: string
  areas: readonly AreaCode[]
  businessProcess: number
  cells: ReadonlyMap<CellKey, CellLocal>
  candidates: ApproverCandidate[]
  /** 「編集はセルクリックから」を出さない (マトリクスが表示されていない入口。 AC-APF-057)。 */
  hideHint?: boolean
}

export const SectionViewModal = ({
  isOpen,
  onClose,
  processLabel,
  areas,
  businessProcess,
  cells,
  candidates,
  hideHint = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const candidateMap = new Map(
    candidates.map((c) => [c.memberId, c.displayName]),
  )

  return (
    <Modal
      footer={
        <Flex
          alignItems="center"
          justifyContent="flex-end"
          gap="8px"
          width="100%"
        >
          <chakra.span marginRight="auto" />
          {/* design の断面ビュー modal footer 「閉じる」は `.btn`(40px) = md。 */}
          <PrimaryButton onClick={onClose} size="md">
            {t('page:adminApprovalFlow.actions.close')}
          </PrimaryButton>
        </Flex>
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="xl"
      title={t('page:adminApprovalFlow.sectionViewModal.title')}
    >
      {/* 「編集はセルクリックから…」は常に読む必要の無い補足なので、 ? + tooltip にする
          (tooltip.md)。 DS Modal の title は string 専用で右隣に置けないため、 本文先頭の
          業務処理名の右に置く。 マトリクスが無い入口 (hideHint) では出さない (AC-APF-057)。 */}
      <Flex alignItems="center" gap="4px" marginBottom="8px">
        <Box textStyle="caption" color="Text.midEmphasis">
          {processLabel}
        </Box>
        {!hideHint && (
          <HelpIcon
            ariaLabel={t('page:adminApprovalFlow.sectionViewModal.editHint')}
            content={t('page:adminApprovalFlow.sectionViewModal.editHint')}
          />
        )}
      </Flex>
      <chakra.table
        textStyle="tableCell"
        width="100%"
        borderCollapse="collapse"
      >
        <thead>
          <tr>
            <chakra.th {...HEADER_CELL_PROPS}>
              {t('page:adminApprovalFlow.sectionViewModal.areaHeader')}
            </chakra.th>
            <chakra.th {...HEADER_CELL_PROPS}>
              {t('page:adminApprovalFlow.sectionViewModal.modeHeader')}
            </chakra.th>
            <chakra.th {...HEADER_CELL_PROPS}>
              {t('page:adminApprovalFlow.sectionViewModal.stageHeader')}
            </chakra.th>
          </tr>
        </thead>
        <tbody>
          {areas.map((area) => {
            const key = makeNoSectionCellKey(businessProcess, area)
            const cell = cells.get(key)
            const mode = cell?.mode ?? ApprovalMode.UNSPECIFIED
            return (
              <tr key={area}>
                <chakra.td
                  {...BODY_CELL_PROPS}
                  minWidth="64px"
                  fontWeight="semibold"
                  whiteSpace="nowrap"
                >
                  {convertAreaLabel(area)}
                </chakra.td>
                <chakra.td {...BODY_CELL_PROPS}>
                  <ApprovalModeTag mode={mode} />
                </chakra.td>
                <chakra.td {...BODY_CELL_PROPS} verticalAlign="top">
                  {mode !== ApprovalMode.APPROVAL ? (
                    <chakra.span color="Text.midEmphasis">—</chakra.span>
                  ) : cell?.stages === undefined || cell.stages.length === 0 ? (
                    <chakra.span
                      textStyle="caption"
                      color="Text.negativeHigh"
                      fontWeight="medium"
                    >
                      {t(
                        'page:adminApprovalFlow.sectionViewModal.approverUnset',
                      )}
                    </chakra.span>
                  ) : (
                    <Flex flexDirection="column" gap="8px">
                      {cell.stages.map((stage, i) => (
                        <StageView
                          key={i}
                          candidateMap={candidateMap}
                          idx={i}
                          stage={stage}
                          t={t}
                        />
                      ))}
                    </Flex>
                  )}
                </chakra.td>
              </tr>
            )
          })}
        </tbody>
      </chakra.table>
    </Modal>
  )
}

type StageViewProps = {
  idx: number
  stage: StageLocal
  candidateMap: Map<string, string>
  t: ReturnType<typeof useTranslation>['t']
}

const StageView = ({
  idx,
  stage,
  candidateMap,
  t,
}: StageViewProps): React.ReactElement => {
  const strategyLabel =
    stage.strategy === ApprovalStrategy.ALL
      ? t('page:adminApprovalFlow.strategy.all')
      : t('page:adminApprovalFlow.strategy.any')

  return (
    <div>
      <Flex alignItems="baseline" gap="8px">
        <chakra.span
          textStyle="body"
          color="Text.highEmphasis"
          fontWeight="bold"
        >
          {t('page:adminApprovalFlow.sectionViewModal.stageNum', {
            num: idx + 1,
          })}
        </chakra.span>
        <chakra.span textStyle="body" color="Text.midEmphasis">
          {strategyLabel}
        </chakra.span>
      </Flex>
      <Flex flexWrap="wrap" gap="4px" marginTop="4px">
        {stage.approverMemberIds.length === 0 ? (
          <chakra.span
            textStyle="caption"
            color="Text.negativeHigh"
            fontWeight="medium"
          >
            {t('page:adminApprovalFlow.sectionViewModal.approverUnset')}
          </chakra.span>
        ) : (
          stage.approverMemberIds.map((id) => {
            const displayName = candidateMap.get(id)
            // 候補不在 (eauth 上で ACTIVE でない) の欠落承認者 (AC-APF-026d /
            // Design Note D4)。StageEditModal と同じ negative 表現・中立語
            // 「(無効)」に揃える (生の member ID (auth0|...) は表示しない)。
            const isMissing = displayName === undefined
            const label =
              displayName ??
              t('page:adminApprovalFlow.stageModal.approverInactiveLabel')
            return isMissing ? (
              <chakra.span
                key={id}
                textStyle="body"
                alignItems="center"
                display="inline-flex"
                padding="4px 10px"
                color="Text.negativeHigh"
                fontWeight="medium"
                background="Surface.negativeLow"
                borderRadius="full"
              >
                {label}
              </chakra.span>
            ) : (
              <chakra.span
                key={id}
                textStyle="body"
                alignItems="center"
                display="inline-flex"
                padding="4px 10px"
                color="Text.midEmphasis"
                background="Surface.secondary"
                borderRadius="full"
              >
                {label}
              </chakra.span>
            )
          })
        )}
      </Flex>
      {/* 欠落承認者を含む段階には StageEditModal の approverMissing と同じ
          再設定誘導文言を出す (D2: 警告のみ、保存・完了はブロックしない)。 */}
      {stage.approverMemberIds.some((id) => !candidateMap.has(id)) && (
        <Box
          textStyle="caption"
          marginTop="4px"
          color="Text.negativeHigh"
          fontWeight="medium"
        >
          {t('page:adminApprovalFlow.stageModal.approverMissing')}
        </Box>
      )}
    </div>
  )
}
